import {
    ComponentPropsWithoutRef,
    Dispatch,
    SetStateAction,
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { createWithEqualityFn as create } from 'zustand/traditional';

interface ContentStateStore {
    content: { [id: string]: boolean | undefined };
    setContentStateById: (id: string, value: boolean) => void;
}

const useContentStateStore = create<ContentStateStore>(set => ({
    content: {},
    setContentStateById: (id, value) =>
        set(state => ({
            ...state,
            content: {
                ...state.content,
                [id]: value,
            },
        })),
}));

const usePersistentContentState = (
    customKey?: string,
): [boolean, Dispatch<SetStateAction<boolean>>] => {
    const { pathname } = useLocation();
    const { content, setContentStateById } = useContentStateStore();
    const key = customKey ?? pathname;
    const savedContent = content[key];
    const [localIsFullWidth, setLocalIsFullWidth] = useState(savedContent ?? false);

    useEffect(() => {
        return () => {
            setContentStateById(key, localIsFullWidth);
        };
    }, [key, localIsFullWidth, setContentStateById]);

    return [localIsFullWidth, setLocalIsFullWidth];
};

interface IContentContext {
    isFullWidth: boolean;
    setIsFullWidth: (value: boolean) => void;
    toggleIsFullWidth: () => void;
}

const ContentContext = createContext<IContentContext>({
    isFullWidth: false,
    setIsFullWidth: () => {},
    toggleIsFullWidth: () => {},
});

interface ContentProps extends ComponentPropsWithoutRef<'div'> {
    dynamicWidthClass?: string;
}

const Content = (props: ContentProps) => {
    const { className = '', dynamicWidthClass = 'max-w-6xl', ...rest } = props;
    const [isFullWidth, setIsFullWidth] = usePersistentContentState();
    const toggleIsFullWidth = useCallback(
        () => setIsFullWidth(isFullWidth => !isFullWidth),
        [setIsFullWidth],
    );
    const providerProps = useMemo(
        () => ({ isFullWidth, setIsFullWidth, toggleIsFullWidth }),
        [isFullWidth, setIsFullWidth, toggleIsFullWidth],
    );

    return (
        <ContentContext.Provider value={providerProps}>
            <div
                data-content-givebutter={''}
                className={twMerge(
                    'mx-auto my-0 w-full transition-[max-width] duration-300',
                    isFullWidth && 'max-w-none',
                    !isFullWidth && dynamicWidthClass,
                    className,
                )}
                {...rest}
            />
        </ContentContext.Provider>
    );
};

export default Content;
export { ContentContext };
export type { IContentContext };
